import React, { useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import MultiCarousel, { ResponsiveType } from "react-multi-carousel";
import { Link, redirect, useNavigate } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import DOMPurify from "dompurify";
import * as yup from "yup";

import TournamentOfferings from "./TournamentOfferings";

import { store } from "../..";
import "../../assets/css/wibb/pages/tournaments.scss";
import ImageModal from "../../components/ImageModal";
import { alertActions } from "../../features/alert/alertSlice";
import { clubhouseActions } from "../../features/clubhouse/clubhouseSlice";
import { selectProducts, shopActions } from "../../features/shop/shopSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { submitContact } from "../../lib/api";
import { CONTENT_IMAGE_URL, TOURNAMENT_LOGO_URL } from "../../lib/globals";
import { MagnifyingGlassIcon } from "../../lib/icons";
import {
    currencyFormat,
    displayFormAlert,
    getTournamentCosts,
    isChildTournament,
} from "../../lib/utils";
import { IMasterTournament } from "../../types/api";
import { ITournamentOverviewContactFormData } from "../../types/forms";
import { ILocalStorageUserData } from "../../types/users";

export const loader = async ({ params }: any) => {
    const { tournamentId } = params;
    let teamsPromise;
    let teams = store.getState().clubhouse.teams;
    let tournaments = store.getState().tournamentmanagement.tournaments;
    let masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;
    const products = store.getState().shop.products;
    const auth = store.getState().auth;

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getTournaments());

    const masterTournamentsPromise = masterTournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    const productsPromise = products.length
        ? Promise.resolve()
        : store.dispatch(shopActions.getProducts());

    if (auth.loggedIn) {
        const user = store.getState().auth
            .user as ILocalStorageUserData["user"];
        teamsPromise = teams.length
            ? Promise.resolve()
            : store.dispatch(
                  clubhouseActions.getTeams({
                      userId: user.id,
                      isRegistering: true,
                  }),
              );
    } else {
        teamsPromise = Promise.resolve();
    }

    await Promise.all([
        productsPromise,
        tournamentsPromise,
        masterTournamentsPromise,
        teamsPromise,
    ]);

    tournaments = store.getState().tournamentmanagement.tournaments;
    masterTournaments = store.getState().tournamentmanagement.masterTournaments;

    store.dispatch(
        tournamentManagementActions.setselectedTournamentId(
            parseInt(tournamentId),
        ),
    );

    const tournament = selectSelectedTournament(store.getState());

    if (isChildTournament(tournament)) {
        return redirect("/tournaments");
    } else {
        return {
            header: `${tournament?.name}`,
        };
    }
};

const TournamentOverview = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);
    const navigate = useNavigate();
    const auth = useAppSelector(state => state.auth);

    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<string>("");

    const { tournaments, masterTournaments } = useAppSelector(
        state => state.tournamentmanagement,
    );
    const products = useAppSelector(selectProducts);
    const tournament = useAppSelector(
        selectSelectedTournament,
    ) as IMasterTournament;

    const dateFormat = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });

    const tournamentCosts = getTournamentCosts(
        tournaments.filter(t => t.parent === tournament.id),
    );

    const handleRegister = () => {
        const tournamentData = {
            tournament: {
                id: tournament.id,
                name: tournament.name,
                startDate: tournament.startDate,
                endDate: tournament.endDate,
            },
        };

        navigate("/register/tournament", { state: tournamentData });
    };

    const tournamentOfferings = masterTournaments.filter(
        t => new Date().getTime() < new Date(t.startDate).getTime() && t.active,
    );

    const contactFormSchema = yup.object().shape({
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email is required")
            .email("Email is invalid"),
        comment: yup.string().required("Comment/Question is required"),
        recaptcha: yup.string().nullable(),
        tournament: yup
            .object()
            .shape({
                label: yup.string().required("Please select the tournament"),
            })
            .required(),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm<ITournamentOverviewContactFormData>({
        mode: "onChange",
        resolver: yupResolver(contactFormSchema),
        defaultValues: {
            firstName: auth.user?.firstName || undefined,
            lastName: auth.user?.lastName || undefined,
            email: auth.user?.email || undefined,
        },
    });

    const onSubmit: SubmitHandler<
        ITournamentOverviewContactFormData
    > = async data => {
        if (verifyCaptcha()) {
            await submitContact(data)
                .then(() => {
                    dispatch(
                        alertActions.success({
                            text: "Thank you for reaching out to us. We will respond as soon as possible.",
                            dismissible: true,
                        }),
                    );
                })
                .catch(() => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem sending your message.`,
                            text: "If the problem continues, please try emailing us directly.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<ITournamentOverviewContactFormData> = (
        data,
    ): void => {
        console.log(data);

        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    const responsive: ResponsiveType = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 1,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 1,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
        },
    };

    return (
        <Container
            className="tournaments overview px-0"
            data-testid="tournamentsContent"
        >
            <Row className="mt-0">
                <Col
                    xs={12}
                    md={{ span: 6, order: 1 }}
                    lg={{ span: 4, order: 1 }}
                    className="align-content-center text-center"
                >
                    {tournament.logo && (
                        <img
                            src={`${TOURNAMENT_LOGO_URL}${tournament.logo}`}
                            alt={tournament.name}
                            className="logo"
                        />
                    )}
                </Col>
                <Col
                    xs={12}
                    md={{ span: 12, order: 3 }}
                    lg={{ span: 4, order: 2 }}
                >
                    {tournament.photos && tournament.photos.length && (
                        <>
                            <MultiCarousel
                                responsive={responsive}
                                swipeable={false}
                                draggable={false}
                                infinite={true}
                                containerClass="multiCarousel"
                            >
                                {[...tournament.photos]
                                    .sort((a, b) => a.order - b.order)
                                    .map((i, idx) => (
                                        <div
                                            className="carouselImgContainer w-100 h-auto"
                                            key={`carousel-image-${idx}`}
                                            onClick={() => {
                                                setModalContent(i.photo);
                                                setModalShow(true);
                                            }}
                                        >
                                            <img
                                                src={`${CONTENT_IMAGE_URL}tournamentPhotos/${i.photo}`}
                                            />
                                            <span className="rollover">
                                                <MagnifyingGlassIcon />
                                            </span>
                                        </div>
                                    ))}
                            </MultiCarousel>
                            <ImageModal
                                show={modalShow}
                                image={modalContent}
                                alt={modalContent}
                                showAlt={false}
                                source={`${CONTENT_IMAGE_URL}tournamentPhotos/`}
                                onHide={() => setModalShow(false)}
                            />
                        </>
                    )}
                </Col>
                <Col
                    xs={12}
                    md={{ span: 6, order: 2 }}
                    lg={{ span: 4, order: 3 }}
                    className="align-content-center px-5"
                >
                    <div className="tournamentInfo pl-2">
                        <h4>
                            {dateFormat.format(new Date(tournament.startDate))}{" "}
                            - {dateFormat.format(new Date(tournament.endDate))}
                        </h4>
                        <h4>
                            {tournaments
                                .filter(t => t.parent === tournament.id)
                                .map(t => (
                                    <span className="ageLevel" key={t.id}>
                                        {t.maxTeams &&
                                        (t.teams
                                            ? t.teams.length >=
                                              parseInt(t.maxTeams?.toString())
                                            : false) ? (
                                            <s>{t.ageLevel}</s>
                                        ) : (
                                            t.ageLevel
                                        )}
                                    </span>
                                ))}
                        </h4>
                        <h4>Competition Level: {tournament.designation}</h4>
                        <h4>
                            <Link
                                to={`/fields/${tournament.field.id}`}
                                target="_blank"
                            >
                                {tournament.field.name}
                            </Link>
                        </h4>
                        <h4 className="price">{tournamentCosts}</h4>
                        {tournament.hotelLink ? (
                            <div className="hotelLink">
                                <Link
                                    to={tournament.hotelLink}
                                    target="_blank"
                                    className="btn btn-primary"
                                >
                                    Discount Lodging Options
                                </Link>
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                    <div className="registrationInfo">
                        <Button
                            className="registerButton"
                            onClick={handleRegister}
                        >
                            Register Now!
                        </Button>
                    </div>
                </Col>
            </Row>
            {tournament.description && (
                <Row className="tournamentDescription">
                    <Col
                        className="px-0"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(tournament.description),
                        }}
                    />
                </Row>
            )}
            <Row>
                <Col className="registrationInfo mb-4">
                    <Button className="registerButton" onClick={handleRegister}>
                        Register Now!
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4>Do you have a question about this tournament?</h4>
                    <Form
                        onSubmit={handleSubmit(onSubmit, onError)}
                        id="contactForm"
                    >
                        {!auth.loggedIn && (
                            <Row>
                                <Form.Group
                                    as={Col}
                                    xs={12}
                                    md={3}
                                    controlId="firstName"
                                >
                                    <Form.Control
                                        type="text"
                                        {...register("firstName", {
                                            required: "First Name is required",
                                        })}
                                        aria-invalid={
                                            errors.firstName ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>First Name</Form.Label>
                                    {errors.firstName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.firstName
                                                .message as string,
                                        })}
                                </Form.Group>
                                <Form.Group
                                    as={Col}
                                    xs={12}
                                    md={3}
                                    controlId="lastName"
                                >
                                    <Form.Control
                                        type="text"
                                        {...register("lastName", {
                                            required: "Last Name is required",
                                        })}
                                        aria-invalid={
                                            errors.lastName ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>Last Name</Form.Label>
                                    {errors.lastName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.lastName
                                                .message as string,
                                        })}
                                </Form.Group>
                                <Form.Group
                                    as={Col}
                                    xs={12}
                                    md={6}
                                    controlId="email"
                                >
                                    <Form.Control
                                        type="email"
                                        {...register("email", {
                                            required:
                                                "Email address  is required",
                                        })}
                                        aria-invalid={
                                            errors.email ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>Email Address</Form.Label>
                                    {errors.email &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.email
                                                .message as string,
                                        })}
                                </Form.Group>
                            </Row>
                        )}

                        <Row>
                            <Form.Control
                                {...register("tournament.label")}
                                type="hidden"
                                value={tournament.name}
                            />
                        </Row>
                        <Row>
                            <Form.Label>Comment *</Form.Label>
                            <Form.Group as={Col} controlId="comment">
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    cols={50}
                                    {...register("comment")}
                                />
                            </Form.Group>
                            {errors.comment &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.comment.message as string,
                                })}
                        </Row>
                        <Row>
                            <Col xs={3}>
                                <Form.Label>CAPTCHA</Form.Label>
                                <ReCAPTCHA
                                    ref={recaptcha}
                                    sitekey={
                                        process.env.RECAPTCHA_SITE_KEY as string
                                    }
                                    onChange={onChangeRecaptcha}
                                />
                                {errors.recaptcha &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.recaptcha
                                            .message as string,
                                    })}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button variant="primary" type="submit">
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>

            <TournamentOfferings
                tournamentOfferings={tournamentOfferings}
                tournaments={tournaments}
            />
        </Container>
    );
};

export default TournamentOverview;
